import React, { useState, useEffect } from 'react';
import { Home, MessageSquare, Sparkles, Trophy, Bell, Sun, Moon, Shield, ChevronsLeft, ChevronLeft, ChevronRight, AlertTriangle, Heart, Gift, Lightbulb, Settings } from 'lucide-react';

import { subscribeToFriendRequests, subscribeToUserChats, subscribeToPendingReportCount, subscribeToSystemConfig, subscribeToUserGiftCodes } from '../services/firestore.js';
import AppearanceSettingsModal from './AppearanceSettingsModal.jsx';

export default function Navigation({ activeTab, onTabChange, user, isCollapsed, onToggleCollapse }) {
    const [notifications, setNotifications] = useState([]);
    const [showSettings, setShowSettings] = useState(false);
    const [unreadChatCount, setUnreadChatCount] = useState(0);
    const [pendingReports, setPendingReports] = useState(0);
    const [systemConfig, setSystemConfig] = useState({ enableReporting: true });
    const [hoveredItem, setHoveredItem] = useState(null);
    const [activeGifts, setActiveGifts] = useState(0);
    const [theme, setTheme] = useState(() => {
        // Load theme from localStorage
        return localStorage.getItem('theme') || 'dark';
    });

    useEffect(() => {
        if (!user?.uid) return;

        // Subscribe to pending reports count (Super Admin)
        let unsubscribeReports = () => { };
        if (user.isSuperAdmin) {
            unsubscribeReports = subscribeToPendingReportCount((count) => {
                setPendingReports(count);
            });
        }

        // Apply theme on mount/update
        document.documentElement.setAttribute('data-theme', theme);

        // Subscribe to friend requests
        const unsubscribeFriends = subscribeToFriendRequests(user.uid, (reqs) => {
            setNotifications(reqs);
        });



        return () => {
            if (typeof unsubscribeFriends === 'function') unsubscribeFriends();
            if (typeof unsubscribeChats === 'function') unsubscribeChats();
        };
    }, [user, theme]);

    const handleTabChange = (tabId) => {
        onTabChange(tabId);
        if (tabId === 'chat') {
            setUnreadChatCount(0);
        }
    };

    useEffect(() => {
        if (!user?.uid) return;
        const unsubscribeChats = subscribeToUserChats(user.uid, (chats) => {
            if (!chats) return;

            const totalUnread = chats.reduce((acc, chat) => {
                return acc + (chat.participants?.[user.uid]?.unreadCount || 0);
            }, 0);

            setUnreadChatCount(totalUnread);
        });
        return unsubscribeChats;
    }, [user]);

    // Subscribe to pending reports count (Super Admin) and system config
    useEffect(() => {
        if (!user?.uid) return;

        // Subscribe to system config (Global)
        const unsubscribeConfig = subscribeToSystemConfig((config) => {
            setSystemConfig(config);
        });

        // Subscribe to pending reports count (Super Admin)
        let unsubscribeReports = () => { };
        if (user.isSuperAdmin) {
            unsubscribeReports = subscribeToPendingReportCount((count) => {
                setPendingReports(count);
            });
        }

        // Subscribe to Gift Codes for Badge
        const unsubscribeGifts = subscribeToUserGiftCodes(user.uid, (codes) => {
            const count = codes.filter(c => c.status === 'active').length;
            setActiveGifts(count);
        });

        return () => {
            unsubscribeConfig();
            unsubscribeReports();
            unsubscribeGifts();
        };
    }, [user]);

    const handleNavClick = (id) => {
        onTabChange(id);
    };

    const toggleTheme = () => {
        const newTheme = theme === 'dark' ? 'light' : 'dark';
        setTheme(newTheme);
        document.documentElement.setAttribute('data-theme', newTheme);
        localStorage.setItem('theme', newTheme);
    };

    const navItems = [
        { id: 'feed', icon: Home, label: 'Feed' },
        { id: 'chat', icon: MessageSquare, label: 'Chat', badge: unreadChatCount > 0 ? unreadChatCount : null },
        { id: 'muse', icon: Sparkles, label: 'Muse AI' },
        { id: 'leaderboard', icon: Trophy, label: 'Leaderboard' },
        // Admin: Desktop Only in main nav
        ...(user?.isAdmin ? [{ id: 'admin', icon: Shield, label: 'Admin', badge: pendingReports > 0 ? pendingReports : null, className: 'mobile-hidden' }] : []),
        // Support: Mobile Only in main nav (swapped with Admin)
        { id: 'support', icon: Heart, label: 'Support', className: 'desktop-hidden mobile-nav-item' },
        {
            id: 'profile',
            icon: (props) => (
                React.createElement('div', { style: { position: 'relative', width: '40px', height: '40px' } },
                    React.createElement('img', {
                        src: user?.avatar || "https://api.dicebear.com/7.x/avataaars/svg?seed=Felix",
                        style: {
                            width: '40px', height: '40px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            transition: 'all 0.2s',
                            border: (activeTab === 'profile' || hoveredItem === 'profile') ? '2px solid var(--primary)' : '2px solid transparent',
                            ...props.style // Pass border/style through
                        }
                    })
                )
            ),
            label: 'Profile',
            badge: activeGifts > 0 ? React.createElement(Gift, { size: 12 }) : null
        }
    ];

    return React.createElement('nav', {
        className: `glass-panel nav-panel ${isCollapsed ? 'collapsed' : ''}`
    },
        React.createElement('div', { className: 'nav-top-section' },
            // Header with notification and theme toggle
            React.createElement('div', {
                className: 'nav-header',
                style: {
                    display: 'flex',
                    flexDirection: isCollapsed ? 'column' : 'column',
                    alignItems: isCollapsed ? 'center' : 'flex-start',
                    gap: isCollapsed ? '20px' : '5px'
                }
            },
                // LOGO
                !isCollapsed ? (
                    React.createElement('img', {
                        src: "/images/logo.png",
                        alt: "SparkInspo Logo",
                        className: "nav-logo mobile-hidden", // Hide on mobile
                        style: {
                            height: '50px',
                            maxWidth: '100%',
                            objectFit: 'contain',
                            display: 'block'
                        },
                        onError: (e) => { e.target.style.display = 'none'; }
                    })
                ) : (
                    React.createElement('img', {
                        src: "/images/logo_Small.png",
                        alt: 'SI',
                        className: "nav-logo-small mobile-hidden", // Hide on mobile
                        style: {
                            width: '50px',
                            height: '50px',
                            objectFit: 'contain'
                        },
                        onError: (e) => { e.target.style.display = 'none'; }
                    })
                ),

                // BUTTON ROW (Notifications + Theme)
                React.createElement('div', {
                    className: 'nav-controls mobile-hidden', // Hidden on mobile (Moved to App.js)
                    style: {
                        display: 'flex',
                        gap: '8px',
                        flexDirection: isCollapsed ? 'column' : 'row',
                        alignSelf: isCollapsed ? 'center' : 'flex-end',
                        marginTop: isCollapsed ? 0 : '5px'
                    }
                },
                    React.createElement('button', {
                        className: 'btn-ghost',
                        onClick: () => handleNavClick('profile'),
                        onMouseEnter: () => setHoveredItem('notifications'),
                        onMouseLeave: () => setHoveredItem(null),
                        style: { padding: '8px', position: 'relative', color: hoveredItem === 'notifications' ? 'var(--primary)' : '#999', transition: 'color 0.2s' },
                        title: 'Notifications'
                    },
                        React.createElement(Bell, { size: 18 }),
                        notifications.length > 0 && React.createElement('span', {
                            style: {
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                background: 'var(--secondary)',
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                fontSize: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                color: 'white'
                            }
                        }, notifications.length)
                    ),
                    React.createElement('button', {
                        className: 'btn-ghost',
                        onClick: () => setShowSettings(true),
                        onMouseEnter: () => setHoveredItem('settings'),
                        onMouseLeave: () => setHoveredItem(null),
                        style: { padding: '8px', color: hoveredItem === 'settings' ? 'var(--primary)' : '#999', transition: 'color 0.2s' },
                        title: 'Display Settings'
                    }, React.createElement(Settings, { size: 18 })),

                    React.createElement('button', {
                        className: 'btn-ghost',
                        onClick: toggleTheme,
                        onMouseEnter: () => setHoveredItem('theme'),
                        onMouseLeave: () => setHoveredItem(null),
                        style: { padding: '8px', color: hoveredItem === 'theme' ? 'var(--primary)' : '#999', transition: 'color 0.2s' },
                        title: theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'
                    }, React.createElement(theme === 'dark' ? Sun : Moon, { size: 18 }))
                )
            ),

            showSettings && React.createElement(AppearanceSettingsModal, { onClose: () => setShowSettings(false) }),

            React.createElement('div', { className: 'nav-items-container' },
                navItems.map(item =>
                    React.createElement('button', {
                        key: item.id,
                        onClick: () => handleNavClick(item.id),
                        onMouseEnter: () => setHoveredItem(item.id),
                        onMouseLeave: () => setHoveredItem(null),
                        className: `btn-ghost nav-item ${activeTab === item.id ? 'active' : ''} ${item.className || ''}`, // Apply conditional classes
                        style: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: isCollapsed ? 'center' : 'flex-start',
                            position: 'relative',
                            // Styles moved to CSS class .nav-item
                            color: (activeTab === item.id || hoveredItem === item.id) ? 'var(--primary)' : '#999',
                        },
                        title: isCollapsed ? item.label : ''
                    },
                        React.createElement(item.icon, {
                            size: 24, // Standardize size
                            className: "nav-icon",
                            style: item.id === 'profile' ? {
                                border: (activeTab === item.id || hoveredItem === item.id) ? '2px solid var(--primary)' : '2px solid transparent',
                                width: '28px', height: '28px', borderRadius: '50%'
                            } : {}
                        }),
                        !isCollapsed && React.createElement('span', { className: "nav-label mobile-hidden" }, item.label),
                        item.badge && React.createElement('span', {
                            className: "nav-badge",
                            style: {
                                background: 'var(--secondary)',
                                color: 'white',
                                color: 'white',
                                fontSize: '10px',
                                minWidth: '16px',
                                height: '16px',
                                padding: '0 4px',
                                borderRadius: '99px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginLeft: isCollapsed ? '0' : 'auto',
                                position: isCollapsed ? 'absolute' : 'static',
                                top: isCollapsed ? '0' : 'auto',
                                right: isCollapsed ? '0' : 'auto',
                                fontWeight: 'bold'
                            }
                        }, item.badge)
                    )
                )
            )
        ),

        React.createElement('div', { className: 'nav-footer' },
            // Support Button (Desktop Only now, since it's in navItems for mobile)
            React.createElement('button', {
                onClick: () => handleNavClick('support'),
                className: `btn-ghost nav-item mobile-hidden ${activeTab === 'support' ? 'active' : ''}`,
                style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    color: (activeTab === 'support' || hoveredItem === 'support') ? 'var(--primary)' : '#999',
                    padding: '12px' // Increased padding
                },
                title: isCollapsed ? 'Support' : ''
            },
                React.createElement(Heart, { size: 24 }),
                !isCollapsed && React.createElement('span', { className: "nav-label" }, "Support")
            ),



            // Report Issue Button
            systemConfig.enableReporting && React.createElement('button', {
                onClick: () => handleNavClick('report-issue'),
                className: 'btn-ghost nav-item mobile-hidden',
                style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    color: (activeTab === 'report-issue') ? 'var(--primary)' : '#999',
                    border: '1px dashed var(--border)',
                    marginTop: '5px',
                    padding: '10px'
                },
                title: isCollapsed ? 'Bug Report' : ''
            },
                React.createElement(AlertTriangle, { size: 18 }),
                !isCollapsed && React.createElement('span', { className: "nav-label" }, "Report Bug")
            ),

            // Collapse Toggle
            React.createElement('button', {
                onClick: onToggleCollapse,
                className: 'btn-ghost collapse-toggle mobile-hidden',
                style: {
                    width: '100%',
                    marginTop: '10px',
                    justifyContent: 'center',
                    display: 'flex',
                    color: '#999',
                    padding: '8px'
                }
            },
                isCollapsed ? React.createElement(ChevronRight, { size: 24 }) : React.createElement('div', { style: { display: 'flex', alignItems: 'center', marginLeft: '-8px' } },
                    React.createElement(ChevronLeft, { size: 24, style: { marginRight: '-18px' } }),
                    React.createElement(ChevronLeft, { size: 24, style: { marginRight: '-18px' } }),
                    React.createElement(ChevronLeft, { size: 24 })
                )
            ),

            !isCollapsed && React.createElement('div', { className: "nav-links-section", style: { marginTop: '15px' } },
                // FREE APPS SECTION
                React.createElement('h4', { className: 'mobile-hidden', style: { fontSize: '11px', textTransform: 'uppercase', color: '#999', marginBottom: '8px' } }, 'Free Apps'),
                React.createElement('div', { className: 'mobile-hidden', style: { display: 'flex', flexWrap: 'wrap', gap: '8px 12px', marginBottom: '15px' } },
                    [
                        { id: 'blender', label: 'Blender', url: 'https://www.blender.org/' },
                        { id: 'gimp', label: 'GIMP', url: 'https://www.gimp.org/' },
                        { id: 'davinci', label: 'DaVinci', url: 'https://www.blackmagicdesign.com/products/davinciresolve' },
                        { id: 'audacity', label: 'Audacity', url: 'https://www.audacityteam.org/' },
                        { id: 'ai-tools', label: 'Google Gemini', url: 'https://gemini.google.com/' }
                    ].map(item =>
                        React.createElement('a', {
                            key: item.id,
                            href: item.url,
                            target: '_blank',
                            rel: 'noopener noreferrer',
                            className: 'text-link',
                            style: { textDecoration: 'none', color: '#999', cursor: 'pointer', fontSize: '11px', padding: 0 }
                        }, item.label)
                    )
                ),

                // LEGAL & INFO SECTION
                React.createElement('h4', { className: 'mobile-hidden', style: { fontSize: '11px', textTransform: 'uppercase', color: '#999', marginBottom: '8px' } }, 'Legal & Info'),
                React.createElement('div', { className: 'legal-links-container', style: { display: 'flex', flexWrap: 'wrap', gap: '8px 12px' } },
                    [
                        { id: 'mission', label: 'Mission' },
                        { id: 'privacy', label: 'Privacy' },
                        { id: 'terms', label: 'Terms' },
                        { id: 'guidelines', label: 'Guidelines' },
                        // Report Bug Text Link (Mobile Only intended, but visible everywhere unless styled)
                        // Using desktop-hidden class to ensure it's mobile only visually if needed, but here simple text link is fine
                        { id: 'report-issue', label: 'Report Bug', className: 'desktop-hidden' }
                    ].map(item =>
                        React.createElement('button', {
                            key: item.id,
                            onClick: () => handleNavClick(item.id),
                            className: `text-link ${item.className || ''}`,
                            style: { background: 'none', border: 'none', textAlign: 'left', color: '#999', cursor: 'pointer', fontSize: '11px', padding: 0 }
                        }, item.label)
                    ),

                    // Admin Shield: Mobile Only, Admin Only, Footer Bottom Left
                    (user?.isAdmin || user?.isSuperAdmin) && React.createElement('button', {
                        onClick: () => handleNavClick('admin'),
                        className: 'desktop-hidden', // Mobile only
                        style: { background: 'none', border: 'none', color: '#999', cursor: 'pointer', padding: 0, marginLeft: '5px' },
                        title: "Admin Dashboard"
                    }, React.createElement(Shield, { size: 14 }))
                )
            )
        )
    );
}
